'use client';

import { CONTENT_EVENTS } from '@repo/analytics';
import type { IRateMovieV1Body, MovieRatingValueType } from '@repo/sdk';
import { trackEvent } from '@repo/web-chrome/lib/analytics';
import { Heart, ThumbsDown, ThumbsUp } from 'lucide-react';
import type React from 'react';
import { TrackingPill } from './tracking-pill';
import type { ITrackingStrings } from './tracking-strings';

interface IRatingThumbsProps {
  collectionId: number;
  rating: MovieRatingValueType | null;
  pending: boolean;
  strings: ITrackingStrings;
  save: (update: IRateMovieV1Body) => Promise<void>;
}

export function RatingThumbs({
  collectionId,
  rating,
  pending,
  strings,
  save,
}: IRatingThumbsProps): React.JSX.Element {
  function rate(value: MovieRatingValueType): void {
    const next = rating === value ? null : value;
    trackEvent(CONTENT_EVENTS.movieRated, { collectionId, rating: next ?? 'cleared' });
    void save({ rating: next });
  }

  return (
    <>
      <span className="text-sm text-muted-foreground">{strings.ratingPrompt}</span>
      <TrackingPill
        icon={ThumbsDown}
        label={strings.rateDisliked}
        active={rating === 'disliked'}
        ariaPressed={rating === 'disliked'}
        disabled={pending}
        onClick={() => rate('disliked')}
      />
      <TrackingPill
        icon={ThumbsUp}
        label={strings.rateLiked}
        active={rating === 'liked'}
        ariaPressed={rating === 'liked'}
        disabled={pending}
        onClick={() => rate('liked')}
      />
      <TrackingPill
        icon={Heart}
        label={strings.rateLoved}
        active={rating === 'loved'}
        ariaPressed={rating === 'loved'}
        disabled={pending}
        onClick={() => rate('loved')}
      />
    </>
  );
}
